import React from "react";
import { useQuery } from "@tanstack/react-query";
import { Phone, Mail, MapPin, Award } from "lucide-react";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import WhatsAppFab from "@/components/WhatsAppFab";
import { api, resolveImageUri } from "@/lib/api";

const TITLES = { about: "About Us", terms: "Terms & Conditions", contact: "Contact Us" };

function ContentBlocks({ content }) {
  const blocks = (content || "").split(/\n\s*\n/);
  return (
    <div className="space-y-5">
      {blocks.map((block, i) => {
        const lines = block.split("\n");
        const first = lines[0]?.trim() || "";
        const isHeading = lines.length > 1 && first.length < 50 && !first.startsWith("•");
        if (isHeading) {
          return (
            <div key={`${first}-${i}`}>
              <h2 className="font-display text-2xl text-ink mb-1">{first}</h2>
              <p className="text-[15px] text-ink/80 leading-relaxed whitespace-pre-line">{lines.slice(1).join("\n")}</p>
            </div>
          );
        }
        return <p key={`${block.slice(0, 24)}-${i}`} className="text-[15px] text-ink/80 leading-relaxed whitespace-pre-line">{block}</p>;
      })}
    </div>
  );
}

export default function InfoPage({ pageKey }) {
  const { data: page, isLoading } = useQuery({ queryKey: ["page", pageKey], queryFn: () => api.getPage(pageKey) });
  const { data: certs = [] } = useQuery({ queryKey: ["certificates"], queryFn: () => api.listCertificates(), enabled: pageKey === "about" });

  return (
    <div className="min-h-screen bg-paper">
      <Header />
      <main className="mx-auto max-w-3xl px-4 sm:px-6 py-10">
        <h1 className="font-display text-4xl sm:text-5xl text-ink mb-6" data-testid="info-title">{TITLES[pageKey]}</h1>
        {isLoading ? (
          <p className="text-muted2">Loading…</p>
        ) : pageKey === "contact" ? (
          <div className="space-y-5">
            {page?.intro && <p className="text-[15px] text-ink/80 leading-relaxed">{page.intro}</p>}
            <div className="grid sm:grid-cols-2 gap-4">
              {page?.phone && <a href={`tel:${page.phone}`} className="flex items-center gap-3 rounded-xl border border-line p-4 hover:border-brand"><Phone className="text-brand" size={20} /><span className="text-sm text-ink">{page.phone}</span></a>}
              {page?.email && <a href={`mailto:${page.email}`} className="flex items-center gap-3 rounded-xl border border-line p-4 hover:border-brand"><Mail className="text-brand" size={20} /><span className="text-sm text-ink break-all">{page.email}</span></a>}
              {page?.address && <div className="flex items-center gap-3 rounded-xl border border-line p-4 sm:col-span-2"><MapPin className="text-brand shrink-0" size={20} /><span className="text-sm text-ink whitespace-pre-line">{page.address}</span></div>}
            </div>
          </div>
        ) : (
          <ContentBlocks content={page?.content} />
        )}

        {pageKey === "about" && certs.length > 0 && (
          <section className="mt-10">
            <h2 className="font-display text-2xl text-ink flex items-center gap-2 mb-4"><Award className="text-brand" size={20} /> Certificates</h2>
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-3" data-testid="certificates-gallery">
              {certs.map((c) => (
                <a key={c.id} href={resolveImageUri(c.image)} target="_blank" rel="noopener noreferrer" className="block rounded-xl overflow-hidden border border-line">
                  <img src={resolveImageUri(c.image)} alt="Certificate" className="w-full h-40 object-cover" />
                </a>
              ))}
            </div>
          </section>
        )}
      </main>
      <Footer />
      <WhatsAppFab />
    </div>
  );
}
